"use client";

import {
  getAuthErrorMessage,
  useAuthUser,
  useLogoutMutation,
} from "@/hooks/auth.hook";
import { AnimatePresence, motion } from "framer-motion";
import {
  HelpCircle,
  Info,
  LayoutDashboard,
  LogOut,
  UserRound,
  UserRoundKey,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

type UserDropdownProps = {
  onLoginClick: () => void;
};

export function UserDropdown({ onLoginClick }: UserDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const t = useTranslations("auth");
  const locale = useLocale();
  const isRTL = locale === "ar";
  const { data: user } = useAuthUser();
  const logoutMutation = useLogoutMutation();

  const isAuthenticated = Boolean(user);
  const isOwner = ["owner", "admin", "superAdmin"].includes(user?.role || "");

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div ref={ref} className="relative">
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={isAuthenticated ? user?.username : t("login")}
        className={`flex items-center justify-center p-2 rounded-full transition-all duration-200 cursor-pointer ${
          isOpen
            ? "bg-neutral-100 dark:bg-white/10 text-neutral-900 dark:text-white"
            : "hover:bg-neutral-100 dark:hover:bg-white/10 text-neutral-600 dark:text-white/60 hover:text-neutral-900 dark:hover:text-white"
        }`}
      >
        <UserRoundKey size={18} />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className={`absolute top-full mt-2 w-56 rounded-2xl bg-white dark:bg-zinc-900 shadow-xl shadow-black/8 border border-neutral-100/80 dark:border-white/10 py-1.5 z-50 ${
              isRTL ? "left-0" : "right-0"
            }`}
          >
            {/* Authenticated user header */}
            {user && (
              <div className="border-b border-neutral-100 px-4 py-3 dark:border-white/10">
                <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-400">
                  {t("loggedInAs")}
                </p>
                <p className="mt-1 text-sm font-semibold text-neutral-900 dark:text-white">
                  {user.username}
                </p>
                {isOwner && (
                  <span className="inline-flex items-center mt-1.5 text-[10px] font-bold uppercase tracking-wider bg-clay-600/10 text-clay-600 px-2 py-0.5 rounded-full">
                    {t("ownerRole")}
                  </span>
                )}
              </div>
            )}

            {/* Owner panel — only for property owners / admins */}
            {isOwner && (
              <Link
                href={`/${locale}/profile/properties`}
                role="menuitem"
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-4 py-2.5 text-sm text-clay-600 hover:bg-clay-600/5 transition-colors duration-150 font-semibold ${isRTL ? "flex-row-reverse" : ""}`}
              >
                <LayoutDashboard size={15} className="shrink-0" />
                <span>{t("myDashboard")}</span>
              </Link>
            )}

            {/* Profile link */}
            {user && (
              <Link
                href={`/${locale}/profile/my-profile`}
                role="menuitem"
                onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5 hover:text-neutral-900 dark:hover:text-white transition-colors duration-150 ${isRTL ? "flex-row-reverse" : ""}`}
              >
                <UserRound
                  size={15}
                  className="text-neutral-400 dark:text-neutral-500 shrink-0"
                />
                <span>{t("viewProfile")}</span>
              </Link>
            )}

            {user && (
              <div className="border-t border-neutral-100 dark:border-white/10 my-1" />
            )}

            {/* Login — only when not authenticated */}
            {!isAuthenticated && (
              <button
                role="menuitem"
                onClick={() => {
                  setIsOpen(false);
                  onLoginClick();
                }}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5 hover:text-neutral-900 dark:hover:text-white transition-colors duration-150 ${isRTL ? "flex-row-reverse" : ""}`}
              >
                <UserRound
                  size={15}
                  className="text-neutral-400 dark:text-neutral-500 shrink-0"
                />
                <span>{t("login")}</span>
              </button>
            )}

            <Link
              href={`/${locale}/help`}
              role="menuitem"
              onClick={() => setIsOpen(false)}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5 hover:text-neutral-900 dark:hover:text-white transition-colors duration-150 ${isRTL ? "flex-row-reverse" : ""}`}
            >
              <HelpCircle
                size={15}
                className="text-neutral-400 dark:text-neutral-500 shrink-0"
              />
              <span>{t("help")}</span>
            </Link>

            <Link
              href={`/${locale}/about`}
              role="menuitem"
              onClick={() => setIsOpen(false)}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-white/5 hover:text-neutral-900 dark:hover:text-white transition-colors duration-150 ${isRTL ? "flex-row-reverse" : ""}`}
            >
              <Info
                size={15}
                className="text-neutral-400 dark:text-neutral-500 shrink-0"
              />
              <span>{t("about")}</span>
            </Link>

            {/* Logout — only when authenticated */}
            {isAuthenticated && (
              <>
                <div className="border-t border-neutral-100 dark:border-white/10 my-1" />
                <button
                  role="menuitem"
                  onClick={async () => {
                    try {
                      await logoutMutation.mutateAsync();
                      toast.success(t("logoutSuccess"));
                      setIsOpen(false);
                    } catch (error) {
                      toast.error(getAuthErrorMessage(error));
                    }
                  }}
                  className={`flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors duration-150 ${isRTL ? "flex-row-reverse" : ""}`}
                >
                  <LogOut size={15} className="shrink-0" />
                  <span>{t("logout")}</span>
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
